import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog } from '@ibe/ui';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { useSoftDeleteRole } from '../api/mutations.js';
import { useRoleDetail } from '../api/queries.js';
import { type RoleDetail } from '../api/schemas.js';

/**
 * One role.
 *
 * Nothing here handles loading, errors, breadcrumbs or the correlation id —
 * `ResourceDetailPage` owns all four, so this file is only the fields this
 * resource has.
 */

interface GroupedPermission {
  readonly resource: string;
  readonly actions: readonly string[];
}

function groupedPermissions(permissions: readonly string[]): readonly GroupedPermission[] {
  const byResource = new Map<string, string[]>();
  for (const permission of permissions) {
    const parts = permission.split('.');
    const action = parts[parts.length - 1] ?? permission;
    const resource = parts.slice(0, -1).join('.') || 'Other';
    const group = byResource.get(resource) ?? [];
    group.push(action);
    byResource.set(resource, group);
  }

  return [...byResource.entries()]
    .map(([resource, actions]) => ({
      resource,
      actions: actions.sort((left, right) => left.localeCompare(right)),
    }))
    .sort((left, right) => left.resource.localeCompare(right.resource));
}

function humanise(resource: string): string {
  return resource.replace(/([A-Z])/g, ' $1').trim();
}

function PermissionList({
  permissions,
}: {
  readonly permissions: readonly string[];
}): React.JSX.Element {
  const groups = groupedPermissions(permissions);

  if (permissions.length === 0) {
    return <p className="text-sm text-muted-foreground">No permissions assigned.</p>;
  }

  return (
    <ul className="space-y-3">
      {groups.map((group) => (
        <li key={group.resource}>
          <h3 className="text-sm font-medium">{humanise(group.resource)}</h3>
          <p className="text-sm text-muted-foreground">{group.actions.join(', ')}</p>
        </li>
      ))}
    </ul>
  );
}

export function RoleDetailPage(): React.JSX.Element {
  const { roleId } = useParams();
  const query = useRoleDetail(roleId ?? '');
  const softDelete = useSoftDeleteRole();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  return (
    <ResourceDetailPage<RoleDetail>
      query={query}
      permission={PERMISSIONS.ROLE_READ}
      parent={{ to: '/roles', label: 'Roles' }}
      title={(data) => data.name}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.ROLE_WRITE}>
            <Button asChild variant="secondary">
              <Link to={`/roles/${roleId ?? ''}/edit`}>
                <Pencil className="h-4 w-4" aria-hidden="true" />
                Edit
              </Link>
            </Button>
          </PermissionGuard>
          {query.data?.isSystem !== true ? (
            <PermissionGuard permission={PERMISSIONS.ROLE_WRITE}>
              <Button
                variant="destructive"
                onClick={() => {
                  setConfirming(true);
                }}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                Delete
              </Button>
            </PermissionGuard>
          ) : null}
        </>
      }
    >
      {(data) => (
        <>
          <DetailGrid>
            <DetailField label="Description">{data.description ?? undefined}</DetailField>
            <DetailField label="System">{data.isSystem ? 'Yes' : 'No'}</DetailField>
            <DetailField label="Permissions">
              <PermissionList permissions={data.permissions} />
            </DetailField>
          </DetailGrid>

          <ConfirmDialog
            open={confirming}
            onOpenChange={setConfirming}
            title="Delete role"
            description={`${data.name} will be removed and every user who held it will lose it. This does not delete their account, only the role.`}
            confirmLabel="Delete"
            destructive
            loading={softDelete.isPending}
            onConfirm={() => {
              softDelete.mutate(data.id, {
                onSuccess: () => {
                  setConfirming(false);
                  void navigate('/roles');
                },
              });
            }}
          />
        </>
      )}
    </ResourceDetailPage>
  );
}
